import React, { useState } from "react";
import {
  Modal,
  Box,
  InputBase,
  IconButton,
  Fade,
  Backdrop,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkIcon from "@mui/icons-material/Link";
import { EXTERNAL_LINK_TYPE } from "../../../constants/applicationConstants";
import { Close } from "@mui/icons-material";
import { DocumentCategory, DocumentFile } from "../../../redux/slice/document/DocumentSlice";

type PopupSearchProps = {
  open: boolean;
  onClose: () => void;
  onSearch: (value: string) => void;
  documentCategoryList: DocumentCategory[] | null;
  documentClick: (file: DocumentFile) => void;
};

const PopupSearch: React.FC<PopupSearchProps> = ({
  open,
  onClose,
  onSearch,
  documentCategoryList,
  documentClick
}) => {
  const [term, setTerm] = useState("");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTerm(value);
    onSearch(value);
  };

  const matchingFiles = term
    ? (documentCategoryList ?? []).flatMap((cat) =>
        cat.subCategories.flatMap((sub) =>
          sub.files.filter((file) =>
            file.documentName.toLowerCase().includes(term.toLowerCase())
          )
        )
      )
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 300,
          sx: { backgroundColor: "rgba(0,0,0,0.6)" },
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "25%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "95%",
            maxWidth: 600,
            bgcolor: colors.grey[900],
            borderRadius: 3,
            p: 2,
            boxShadow: 24,
            maxHeight: "80vh",
            overflowY: "auto",
            border : 2,
            borderColor : colors.grey[700]
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: colors.grey[800],
            }}
          >
            <SearchIcon sx={{ color: colors.grey[400] }} />
            <InputBase
              autoFocus
              placeholder="Search..."
              fullWidth
              value={term}
              onChange={handleSearchChange}
              sx={{
                fontSize: "18px",
                color: colors.grey[100],
                flex: 1,
              }}
            />
            {term !== "" && (
              <IconButton
                size="small"
                onClick={() => {
                  setTerm("");
                  onSearch("");
                }}
              >
                <Close sx={{ fontSize: 20, color: colors.grey[400] }} />
              </IconButton>
            )}
          </Box>

          {term && (
            <Box mt={2}>
              {matchingFiles.length === 0 ? (
                <Typography sx={{ color: colors.grey[400], mt: 2 }}>
                  No matching documents found.
                </Typography>
              ) : (
                matchingFiles.map((file, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      backgroundColor: colors.grey[800],
                      p: 1,
                      borderRadius: 2,
                      mt: 1,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      documentClick(file)
                    }}
                  >
                    {file.externalLinkType ===
                    EXTERNAL_LINK_TYPE.EXTERNAL_URL ? (
                      <LinkIcon sx={{ color: colors.info[400] }} />
                    ) : (
                      <DescriptionIcon sx={{ color: colors.success[400] }} />
                    )}
                    <Typography color={colors.grey[100]}>
                      {file.documentName}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default PopupSearch;
